import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CaretDownOutlined, SettingOutlined } from "@ant-design/icons";
import { theme, Typography } from "antd";
import { createStyles } from "antd-style";

import { SettingsDrawer } from "@av/components/organisms/SettingsDrawer";
import { Dropdown, type ItemType } from "../atoms/Dropdown";
import { UserAvatar } from "../molecules/EmployeeAvatar";

import type { DisplayUser } from "./type";

const useStyles = createStyles(({ token, css }) => ({
  container: css`
    display: flex;
    padding: ${token.paddingXS}px;
    border-radius: 12px;
    background-color: ${token.colorPrimaryBg};
  `,
  avatar: css`
    margin-right: ${token.paddingXS}px;
  `,
  arrowIcon: css`
    font-size: ${token.paddingSM}px;
    margin-left: ${token.paddingXS / 2}px;
    color: ${token.colorPrimary};
  `,
  text: css`
    color: ${token.colorText};
  `,
}));

type ProfileDropdownProps = {
  onLogout: () => void;
  user?: DisplayUser;
  isDemo: boolean;
};

function ProfileDropdown(props: ProfileDropdownProps) {
  const { Text } = Typography;
  const [isSettingsOpen, setIsSettingOpen] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const { styles } = useStyles();
  const { t } = useTranslation();
  const { user, isDemo } = props;
  const { token } = theme.useToken();

  const items: ItemType[] = [
    {
      key: "settings",
      label: t("common.components.organisms_settingsDrawer_title"),
      icon: <SettingOutlined />,
      ...{ "data-testid": "user-profile-dropdown-settings" },
      onClick: () => {
        setIsSettingOpen(true);
      },
    },
    {
      key: "logout",
      label: t("hr.profile.signOut"),
      ...{ "data-testid": "user-profile-dropdown-logout" },
      onClick: () => {
        props.onLogout();
      },
    },
  ];

  return (
    <>
      <Dropdown
        menu={{ items }}
        trigger={["click"]}
        open={isMenuOpen}
        onOpenChange={setIsMenuOpen}
        className={styles.container}
        testIdPrefix="user-account"
      >
        <a
          onClick={(e) => e.preventDefault()}
          aria-expanded={isMenuOpen}
          data-testid="user-profile-dropdown"
        >
          <UserAvatar
            isDemo={isDemo}
            testIdPrefix="header-profile-dropdown"
            info={{ ...user, avatarColor: token.colorPrimaryBorder }}
            size="small"
            className={styles.avatar}
          />
          <Text strong className={styles.text}>
            {user?.displayName}
          </Text>
          <CaretDownOutlined className={styles.arrowIcon} />
        </a>
      </Dropdown>
      <SettingsDrawer isOpen={isSettingsOpen} onClose={() => setIsSettingOpen(false)} />
    </>
  );
}

export { ProfileDropdown };
